import React from 'react';
import { Zap, Shield, Clock, CheckCircle, Eye, Users, Car, Timer, DollarSign } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const VehicleEntryExitManagementSystem: React.FC = () => {
    useDynamicSEO({
      title: "Vehicle Entry Exit Management System | Automated Parking App",
      description: "Automate vehicle entry & exit with instant digital scanning, real-time monitoring & zero congestion. A perfect app for smooth traffic flow & secure operations.",
          keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
      ogTitle: "Vehicle Entry Exit Management System | Automated Parking App",
      ogDescription: "Automate vehicle entry & exit with instant digital scanning, real-time monitoring & zero congestion. A perfect app for smooth traffic flow & secure operations."
    });

  const handleWhatsAppDemo = () => {
    const phoneNumber = "919384376329";
    const message = "Hi! I'm interested in learning more about Park Hive's Vehicle Entry Exit Management System. Can you please provide me with a demo?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const systemBenefits = [
    "Vehicles move in and out without delay",
    "Identity is verified instantly",
    "Charges are accurate and automatic",
    "Records are digitally secured",
    "Revenue remains fully traceable",
    "Security stays uncompromised"
  ];

  const features = [
    {
      icon: <Zap className="h-6 w-6 text-blue-600" />,
      title: "Instant vehicle identification with smart scan technology",
      description: "Advanced number plate recognition"
    },
    {
      icon: <Timer className="h-6 w-6 text-green-600" />,
      title: "Automatic time tracking from entry to exit",
      description: "Precise duration calculation"
    },
    {
      icon: <DollarSign className="h-6 w-6 text-purple-600" />,
      title: "Digital payment support for quick settlement and zero disputes",
      description: "Seamless transaction processing"
    },
    {
      icon: <Eye className="h-6 w-6 text-orange-600" />,
      title: "Real-time visibility of every ongoing and completed session",
      description: "Complete operational transparency"
    },
    {
      icon: <Users className="h-6 w-6 text-indigo-600" />,
      title: "Secure user-level access for staff accountability",
      description: "Role-based access control"
    },
    {
      icon: <Car className="h-6 w-6 text-teal-600" />,
      title: "Multi-vehicle type support including 2W, 3W, 4W, vans, buses, and trucks",
      description: "Comprehensive vehicle category handling"
    }
  ];

  const additionalFeatures = [
    "Custom pricing rules for weekend, hourly, daily, and monthly billing",
    "Live monitoring of capacity and occupancy",
    "Offline capability for unstable connections",
    "Exportable logs and automated reporting"
  ];

  const applications = [
    "Commercial mall & multiplex parking",
    "Airports, railway & bus terminals",
    "Hospitals & universities",
    "Gated residential communities",
    "Tech parks & corporate campuses",
    "Event & exhibition grounds",
    "Stadiums & public gatherings",
    "Truck & logistics yards"
  ];

  const faqs = [
    {
      question: "Do we need expensive hardware to use Park Hive?",
      answer: "No — a smartphone is enough. Hardware can be integrated later if needed."
    },
    {
      question: "Can this handle peak traffic during events and weekends?",
      answer: "Yes — the system is engineered for high-density movement."
    },
    {
      question: "Will staff require training?",
      answer: "Minimal — they can operate the app confidently within minutes."
    },
    {
      question: "Can I monitor operations remotely?",
      answer: "Yes — everything updates live even if you aren't physically present."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Vehicle Entry Exit <span className="text-blue-600">Management System</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-8">
              A Smart, Automated Vehicle Entry Exit Management System for High-Speed, High-Accuracy Parking Operations
            </h2>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Every vehicle entering or exiting your parking facility is a moment of responsibility. 
                A moment where speed and accuracy matter. A moment that defines efficiency, safety, and customer trust.
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-red-900 mb-3">❌ The Chaos of Manual Operations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-red-800">
                  <div>• Long queues</div>
                  <div>• Staff confusion</div>
                  <div>• Manual mistakes</div>
                  <div>• Incorrect billing</div>
                  <div>• Unverified access</div>
                  <div>• Lost tickets</div>
                </div>
                <p className="mt-4 font-medium">Every delay becomes a complaint. Every missed record becomes a financial risk.</p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-3">✅ The Park Hive Solution</h3>
                <p className="text-blue-800 text-lg">
                  Modern parking demands a system that eliminates friction and replaces uncertainty with precision. 
                  Park Hive delivers intelligent automation designed to accelerate movement and provide complete visibility.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl p-8 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <h4 className="text-xl font-bold mb-2">No confusion</h4>
                  <p>Clear, automated processes</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">No manual dependency</h4>
                  <p>Technology-driven operations</p>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">No unanswered questions</h4>
                  <p>Complete transparency</p>
                </div>
              </div>
              <div className="text-center mt-6">
                <p className="text-2xl font-bold">Just seamless flow and total control.</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Request a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="border border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-50 transition-colors duration-200"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Entry & Exit Management */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Why Entry & Exit Management Is the Heart of Parking Control
            </h2>
            
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 mb-12">
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                Entry and exit are the pressure points where everything can collapse.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white border border-red-200 rounded-lg p-4 text-center">
                  <h4 className="font-bold text-red-900 mb-2">If the gate gets stuck</h4>
                  <p className="text-red-700">The parking lot loses rhythm</p>
                </div>
                <div className="bg-white border border-orange-200 rounded-lg p-4 text-center">
                  <h4 className="font-bold text-orange-900 mb-2">If the process slows</h4>
                  <p className="text-orange-700">Frustration spreads</p>
                </div>
                <div className="bg-white border border-red-200 rounded-lg p-4 text-center">
                  <h4 className="font-bold text-red-900 mb-2">If the data fails</h4>
                  <p className="text-red-700">Revenue disappears</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
                A powerful entry-exit system ensures that:
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {systemBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center bg-green-50 rounded-lg p-4">
                    <CheckCircle className="h-6 w-6 text-green-600 mr-4 flex-shrink-0" />
                    <span className="text-green-900 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  When gates flow smoothly, the entire parking ecosystem runs effortlessly.
                </h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              How the Vehicle Entry Exit System Works
            </h2>
            
            <div className="space-y-8">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">1</div>
                  <h4 className="text-xl font-bold text-blue-900">Vehicle Entry</h4>
                </div>
                <p className="text-blue-800 text-lg leading-relaxed">
                  As a vehicle arrives, the operator simply scans the number plate using the Park Hive mobile app. 
                  The system automatically detects the vehicle type and starts timing the session. No handwritten slips, 
                  no guessing, no manual calculators.
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">2</div>
                  <h4 className="text-xl font-bold text-green-900">Vehicle Exit</h4>
                </div>
                <p className="text-green-800 text-lg leading-relaxed">
                  When the vehicle exits, another scan instantly calculates the exact parking duration and billing amount 
                  based on configured pricing rules. The customer pays digitally and moves on without waiting.
                </p>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">3</div>
                  <h4 className="text-xl font-bold text-purple-900">Real-Time Control</h4>
                </div>
                <p className="text-purple-800 text-lg leading-relaxed">
                  The owner watches everything live: Entry time, exit time, tariff, operator actions, revenue totals — 
                  nothing is hidden, nothing is lost.
                </p>
              </div>
            </div>
            
            <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8 text-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="text-xl font-bold mb-2">Speed becomes the standard</h4>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Accuracy becomes automatic</h4>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Control becomes effortless</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Features that Parking Owners Depend On
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12">
              Park Hive gives owners everything they need to manage entry and exit professionally and without stress:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                  <div className="flex items-start mb-3">
                    <div className="mr-4 mt-1">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Additional Capabilities:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {additionalFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="text-center bg-gradient-to-r from-green-100 to-blue-100 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Complete Accountability</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-gray-900">Every movement is recorded</h4>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-gray-900">Every transaction is verifiable</h4>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-bold text-gray-900">Every operator is accountable</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real-World Challenges */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Designed for Real-World Parking Challenges
            </h2>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
              <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed">
                Whether your parking space handles corporate crowds every morning, multiplex rush every weekend, 
                emergency hospital movement round the clock, festival traffic, or seasonal tourism peaks — 
                the system keeps movement smooth under pressure.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-bold text-blue-900">Corporate Crowds</h4>
                  <p className="text-blue-700 text-sm">Morning rush management</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <Car className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-bold text-green-900">Multiplex Rush</h4>
                  <p className="text-green-700 text-sm">Weekend peak handling</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <Shield className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <h4 className="font-bold text-red-900">Emergency Movement</h4>
                  <p className="text-red-700 text-sm">24/7 hospital operations</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-bold text-purple-900">Festival Traffic</h4>
                  <p className="text-purple-700 text-sm">Event crowd control</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <Eye className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                  <h4 className="font-bold text-orange-900">Tourism Peaks</h4>
                  <p className="text-orange-700 text-sm">Seasonal volume management</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 text-center">
                  <Zap className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                  <h4 className="font-bold text-teal-900">All Scenarios</h4>
                  <p className="text-teal-700 text-sm">Consistent performance</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl p-8">
              <h3 className="text-2xl font-bold text-center mb-6">
                A professionally managed entry-exit process means:
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-center">
                <div className="bg-white/20 rounded-lg p-4">
                  <h4 className="font-bold text-xl mb-1">No queues</h4>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <h4 className="font-bold text-xl mb-1">No arguments</h4>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <h4 className="font-bold text-xl mb-1">No fraud</h4>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <h4 className="font-bold text-xl mb-1">No lost revenue</h4>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <h4 className="font-bold text-xl mb-1">No chaos</h4>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <p className="text-xl">
                  The difference between organized flow and stressful gridlock is a few seconds.<br />
                  <span className="font-bold text-yellow-300">Park Hive saves those seconds.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Benefits from Automated Entry & Exit
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {applications.map((application, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm flex items-center border border-gray-200">
                  <CheckCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{application}</span>
                </div>
              ))}
            </div>
            
            <div className="text-center bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                When vehicle flow matters, automation wins.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Park Hive */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
              Why Parking Operators Choose Park Hive
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-xl p-8 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">🔍 Replaces Uncertainty</h4>
                <p className="text-gray-700">
                  Because it replaces uncertainty with clarity and removes human dependency from critical control points.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">💰 Protects Revenue</h4>
                <p className="text-gray-700">
                  Because it captures every rupee honestly and transparently with complete traceability.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">🚀 Enables Growth</h4>
                <p className="text-gray-700">
                  Because it makes operations scalable, professional, and future-ready for expansion.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">👥 Empowers Staff</h4>
                <p className="text-gray-700">
                  Technology doesn't replace people — it empowers them to perform confidently.
                </p>
              </div>
            </div>
            
            <div className="bg-blue-600 text-white rounded-xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-4">Park Hive does exactly that.</h3>
              <p className="text-xl">Empowering parking operations with intelligent automation.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Request a Live Demo
              </button>
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200"
              >
                Request a Demo
              </button>
            </div>
            
            <p className="text-gray-600 mt-6">
              See how fast the difference feels.<br />
              Watch entry, exit, billing, and reporting in real time.<br />
              Experience true operational control in the palm of your hand.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final Thought */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">Final Thought</h2>
            
            <div className="space-y-8 text-lg leading-relaxed">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-blue-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-blue-300 mb-2">⚖️ Parking is judged at the gate</h4>
                  <p>First impressions matter most</p>
                </div>
                <div className="bg-green-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-green-300 mb-2">🚀 Speed builds reputation</h4>
                  <p>Fast operations create satisfaction</p>
                </div>
                <div className="bg-purple-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-purple-300 mb-2">🎯 Precision protects revenue</h4>
                  <p>Accuracy ensures profitability</p>
                </div>
                <div className="bg-orange-800/30 rounded-lg p-6">
                  <h4 className="font-bold text-orange-300 mb-2">🤝 Transparency builds trust</h4>
                  <p>Clear processes create confidence</p>
                </div>
              </div>
              
              <div className="space-y-4 mt-12">
                <p className="text-xl">
                  A Vehicle Entry Exit Management System is not just a tool —
                </p>
                <p className="text-2xl font-bold text-yellow-300">
                  it is the backbone of modern parking efficiency.
                </p>
                <p className="text-2xl font-bold">
                  Park Hive makes it easy
                </p>
              </div>
            </div>
            
            <div className="mt-12">
              <button 
                onClick={handleWhatsAppDemo}
                className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-medium hover:bg-gray-100 transition-colors duration-200 inline-flex items-center"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
                </svg>
                Transform your entry-exit process into a smooth, intelligent, automated workflow
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VehicleEntryExitManagementSystem;